import { FormikProvider, useFormik } from 'formik';
import isEqual from 'lodash/isEqual';
import { useCallback } from 'react';
import { useGetFormTagsListQuery } from '@/modules/admin-dashboard/queries/form-tags.ts';
import { useUpdateFormMutation } from '@/modules/admin-dashboard/queries/forms.ts';
import { BaseModalProps, Button, CustomCheckbox, ModalContentWrapper, Spinner } from 'ui';
import { FormResponse } from 'compliance-api';

interface FormTagsModalProps extends BaseModalProps {
  form: FormResponse;
}

const FormTagsModal = ({ form, open, onClose }: FormTagsModalProps) => {
  const { data: tagsData, isLoading } = useGetFormTagsListQuery();

  const { mutateAsync, isLoading: isSaving } = useUpdateFormMutation();

  const formik = useFormik({
    initialValues: { ids: (form.form_tags || []).map(({ id }) => id) },
    onSubmit: async values => {
      await mutateAsync({ id: form.id, tag_ids: values.ids });
      onClose();
    },
  });

  const handleToggleSelection = useCallback(
    (id: number) => {
      const checked = formik.values.ids.includes(id);

      const newValue = checked
        ? formik.values.ids.filter(idx => idx !== id)
        : [...formik.values.ids, id];

      formik.setFieldValue('ids', newValue);
    },
    [formik],
  );

  return (
    <ModalContentWrapper open={open} onClose={onClose} title={`Manage "${form.name}" categories`}>
      {isLoading && (
        <div className="w-full h-[200px] flex justify-center items-center">
          <Spinner size="md" />
        </div>
      )}

      {!isLoading && !!tagsData?.tags?.length && (
        <FormikProvider value={formik}>
          <div className="space-y-1.5">
            {tagsData.tags.map(tag => (
              <div key={tag.id} className="flex gap-2">
                <CustomCheckbox
                  value={tag.id}
                  checked={formik.values.ids.includes(tag.id)}
                  onChange={handleToggleSelection.bind(null, tag.id)}
                />
                <div
                  className="flex-1 hover:underline cursor-pointer"
                  onClick={handleToggleSelection.bind(null, tag.id)}
                >
                  {tag.name}
                </div>
              </div>
            ))}
          </div>

          <Button
            onClick={formik.submitForm}
            className="mt-4 max-h-[250px]"
            disabled={isEqual(formik.initialValues, formik.values) || isSaving}
            loading={isSaving}
          >
            Save changes
          </Button>
        </FormikProvider>
      )}
      {/*todo: empty state*/}
    </ModalContentWrapper>
  );
};

export default FormTagsModal;
